import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiJobFunction } from '#/api/types/ApiJobFunction';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { SearchHitCarousel } from '#/components/SearchHitCarousel/SearchHitCarousel';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { useSearchLocationOrEmpty } from '#/modules/search/contexts/SearchContext';
import { useSearchAlgoliaRecommendedJobs } from '#/modules/search/hooks/useSearchAlgoliaRecommendedJobs';
import { searchGetJobsRoutePath } from '#/modules/search/jobs/routing/searchGetJobsRoutePath';

type StandardCmsRecommendedJobsProps = {
  headline: string | null | undefined;
  layout: 'SLIM' | 'REGULAR';
  jobFunction: ApiJobFunction | null | undefined;
  areaOfFocus: ApiAreaOfFocus | null | undefined;
  recommendedLocale?: I18nLocale | 'all';
  source: 'RECOMMENDED_JOBS_CHECKBOX' | 'RECOMMENDED_JOBS_BLOCK';
};

export function CmsStandardRecommendedJobs({
  headline,
  layout,
  jobFunction,
  areaOfFocus,
  recommendedLocale,
  source,
}: StandardCmsRecommendedJobsProps) {
  const { i18n, locale } = useI18n();

  const searchLocation = useSearchLocationOrEmpty();

  const { data: recommendedJobs, isLoading } = useSearchAlgoliaRecommendedJobs(
    searchLocation,
    {
      onlyWithLogo: layout === 'REGULAR',
      jobFunction,
      areaOfFocus,
      locale: recommendedLocale === 'all' ? undefined : locale,
    },
  );

  const additionalAnalyticsProps: Record<string, string> = {};

  if (jobFunction) additionalAnalyticsProps.job_function = jobFunction;
  if (areaOfFocus) additionalAnalyticsProps.area_of_focus = areaOfFocus;

  if (isLoading) return <LoadingSection />;

  return (
    <SearchHitCarousel
      headline={headline}
      results={recommendedJobs}
      seeAll={{
        /* TRANSLATORS: Link to job search */
        text: i18n('See all jobs'),
        action: {
          type: 'link',
          url: searchGetJobsRoutePath({
            locale,
            filtersByName: {
              functions: jobFunction ? [jobFunction] : undefined,
              areasOfFocus: areaOfFocus ? [areaOfFocus] : undefined,
            },
            embedSearchWidgetId: null,
          }),
        },
      }}
      listingType="JOB"
      carouselLocation="CMS Page"
      layout={layout}
      source={source}
      additionalAnalyticsProps={additionalAnalyticsProps}
    />
  );
}
